import React, { useState, useMemo } from 'react';
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  Divider,
  List,
  ListItem,
  Paper,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import Modal from '../../../components/Modals/Modal/Modal';
import { Ad, AdStatus } from '../../../types/ads.types';
import { formatCurrency } from '../../../utils/formatters';

interface BulkStatusModalProps {
  open: boolean;
  selectedAds: Ad[];
  onClose: () => void;
  onConfirm: (newStatus: 'ACTIVE' | 'PAUSED') => Promise<void>;
}

interface StatusGroup {
  status: AdStatus;
  ads: Ad[];
  totalSpend: number;
  totalRevenue: number;
}

/**
 * Enhanced BulkStatusModal - Modal for updating status with detailed view
 *
 * Features:
 * - Shows current status distribution (ACTIVE/PAUSED counts and spend)
 * - Lists all selected ads grouped by current status
 * - Shows ad name, campaign › adset hierarchy, spend/revenue
 * - Action buttons to change all selected ads to ACTIVE or PAUSED
 */
export const BulkStatusModal: React.FC<BulkStatusModalProps> = ({
  open,
  selectedAds,
  onClose,
  onConfirm,
}) => {
  const [loading, setLoading] = useState(false);
  const [newStatus, setNewStatus] = useState<'ACTIVE' | 'PAUSED'>('ACTIVE');

  // Group ads by status
  const statusGroups = useMemo(() => {
    const groups: StatusGroup[] = [];
    const activeAds: Ad[] = [];
    const pausedAds: Ad[] = [];

    let activeSpend = 0;
    let activeRevenue = 0;
    let pausedSpend = 0;
    let pausedRevenue = 0;

    selectedAds.forEach((ad) => {
      if (ad.status === 'ACTIVE') {
        activeAds.push(ad);
        activeSpend += ad.spend;
        activeRevenue += ad.revenue;
      } else if (ad.status === 'PAUSED') {
        pausedAds.push(ad);
        pausedSpend += ad.spend;
        pausedRevenue += ad.revenue;
      }
    });

    if (activeAds.length > 0) {
      groups.push({
        status: 'ACTIVE',
        ads: activeAds,
        totalSpend: activeSpend,
        totalRevenue: activeRevenue,
      });
    }

    if (pausedAds.length > 0) {
      groups.push({
        status: 'PAUSED',
        ads: pausedAds,
        totalSpend: pausedSpend,
        totalRevenue: pausedRevenue,
      });
    }

    return groups;
  }, [selectedAds]);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm(newStatus);
      onClose();
    } catch (error) {
      // Error handling done in parent
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const getStatusColor = (status: AdStatus) => {
    return status === 'ACTIVE' ? '#10b981' : '#6b7280';
  };

  const getStatusLabel = (status: AdStatus) => {
    return status === 'ACTIVE' ? 'ACTIVE' : 'PAUSED';
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Bulk Status Update"
      maxWidth={900}
    >
      {/* Header */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Updating <strong>{selectedAds.length} selected ads</strong>
      </Typography>

      {/* Current Status Distribution */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Current Status Distribution
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {statusGroups.map((group) => (
            <Paper
              key={group.status}
              variant="outlined"
              sx={{
                p: 2,
                flex: 1,
                minWidth: 200,
                bgcolor: group.status === 'ACTIVE' ? 'success.50' : 'grey.100',
                borderColor: getStatusColor(group.status),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: getStatusColor(group.status),
                  }}
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {getStatusLabel(group.status)}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                {group.ads.length} ad{group.ads.length !== 1 ? 's' : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatCurrency(group.totalSpend)} spent
              </Typography>
            </Paper>
          ))}
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Ads List Grouped by Status */}
      <Box sx={{ maxHeight: 400, overflowY: 'auto', mb: 3 }}>
        {statusGroups.map((group) => (
          <Box key={group.status} sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 1,
                fontWeight: 600,
                color: group.status === 'ACTIVE' ? 'success.main' : 'text.secondary',
              }}
            >
              {getStatusLabel(group.status)} - {group.ads.length} ad{group.ads.length !== 1 ? 's' : ''}
            </Typography>

            <List sx={{ p: 0 }}>
              {group.ads.map((ad) => (
                <ListItem
                  key={ad.adId}
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    mb: 0.5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1, minWidth: 0, mr: 2 }}>
                      {/* Ad Name */}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {ad.adName}
                      </Typography>

                      {/* Campaign › Adset hierarchy */}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {ad.campaignName} › {ad.adsetName}
                      </Typography>
                    </Box>

                    {/* Spend / Revenue */}
                    <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: ad.spend > 0 ? 'text.primary' : 'text.secondary',
                        }}
                      >
                        {formatCurrency(ad.spend)} / {formatCurrency(ad.revenue)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Spend / Revenue
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Status Selection */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 180 }}>
            Change all selected ads to:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value as 'ACTIVE' | 'PAUSED')}
              disabled={loading}
              renderValue={(value) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: value === 'ACTIVE' ? '#10b981' : '#6b7280',
                    }}
                  />
                  <Typography>{value === 'ACTIVE' ? 'Active' : 'Paused'}</Typography>
                </Box>
              )}
            >
              <MenuItem value="ACTIVE">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#10b981',
                    }}
                  />
                  <Typography>Active</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="PAUSED">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#6b7280',
                    }}
                  />
                  <Typography>Paused</Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={handleClose} disabled={loading} color="inherit" variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading && <CircularProgress size={16} />}
        >
          {loading ? 'Updating...' : 'OK'}
        </Button>
      </Box>
    </Modal>
  );
};

export default BulkStatusModal;
